import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: React.ReactNode;
  /** Al cambiar (p. ej. la ruta), el boundary se reinicia y vuelve a intentar renderizar. */
  resetKey?: string;
  /** `page` conserva el layout (menú) y muestra el aviso en el área de contenido; `app` ocupa toda la pantalla. */
  variant?: 'page' | 'app';
}

interface State {
  error: Error | null;
}

const RELOAD_FLAG = 'cn_chunk_reload';

/** Tras un despliegue, una pestaña abierta pide un chunk con hash antiguo que ya no existe. */
function isChunkLoadError(error: Error): boolean {
  const msg = `${error?.name ?? ''} ${error?.message ?? ''}`;
  return /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|error loading dynamically imported module/i.test(msg);
}

/**
 * Evita la pantalla en blanco ante un error de render: muestra un aviso con «Reintentar» y
 * «Recargar». Si el error es un chunk obsoleto por un despliegue nuevo, recarga la página una sola
 * vez de forma automática.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    if (isChunkLoadError(error)) {
      try {
        if (sessionStorage.getItem(RELOAD_FLAG) !== '1') {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
        }
      } catch {
        /* sessionStorage no disponible: queda el botón manual */
      }
    }
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private retry = () => this.setState({ error: null });

  private reload = () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* ignorar */
    }
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const stale = isChunkLoadError(error);
    const full = this.props.variant === 'app';
    return (
      <div
        role="alert"
        className={`flex items-center justify-center p-6 ${full ? 'min-h-screen bg-slate-50' : 'min-h-[50vh]'}`}
      >
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl shadow-sm p-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            <AlertTriangle size={24} />
          </div>
          <h2 className="text-lg font-semibold text-slate-800">
            {stale ? 'Hay una versión nueva de la aplicación' : 'Algo salió mal en esta pantalla'}
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            {stale
              ? 'Recarga la página para continuar con la versión actualizada.'
              : 'Puedes reintentar o recargar la página. Si el problema continúa, avisa a soporte indicando qué estabas haciendo.'}
          </p>
          {import.meta.env.DEV && (
            <pre className="mt-3 max-h-40 overflow-auto rounded bg-slate-100 p-2 text-left text-xs text-slate-600">
              {error.message}
            </pre>
          )}
          <div className="mt-5 flex justify-center gap-2">
            {!stale && (
              <button
                onClick={this.retry}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Reintentar
              </button>
            )}
            <button
              onClick={this.reload}
              className="px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg shadow-sm transition-colors inline-flex items-center gap-2"
            >
              <RefreshCw size={14} /> Recargar
            </button>
          </div>
        </div>
      </div>
    );
  }
}
